import * as React from 'react';
import arial from '../../../style/Arial/arial.ttf';
import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { IProjectMacro1 } from '../../../Macro1/store/project/projectslice';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import PDFHeader from '../PDFLayout/PDFHeader';
import { styles } from '../PDFStyle';
import ProjectInformation from '../PDFComponents/ProjectInformation';
import PDFFooter from '../PDFLayout/PDFFooter';
import { conversionLabel } from '../../../conversion';
import PDFTable from '../PDFTable/PDFTable';
import checkImage from '../../../images/check.jpg';
import crossImage from '../../../images/x-mark.jpg';
import { getProjectSiteEnvLevelAsync } from '../../../api/store/BeMaccaferriStoreHelper';
import { useEffect } from 'react';
import { display } from '@mui/system';

interface IPDFProps {
    project: IProjectMacro1;
    projectInfo: IProjectInfoForm;
    results: any;
}


const PDFMacro1: React.FunctionComponent<IPDFProps> = props => {
    const { t } = useTranslation();
    const { project, projectInfo, results } = props;
    const imgSrc = localStorage.getItem('graphic') ?? '';
    const [selectedSiteEnvLevel, setSelectedSiteEnvLevel] = React.useState<any>(null);

    let component: JSX.Element;

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.unit == 'Metric' ? 0 : 1] + ']';
    };

    useEffect(() => {
        const fetchSiteEnvLevel = async () => {
            try {
                const response = await getProjectSiteEnvLevelAsync(window.localStorage.getItem("i18nextLng") ?? 'en-US');
                const siteEnvLevel = response.data.find((el) => el.id === project.siteEnvironmentalLevelId);
                setSelectedSiteEnvLevel(siteEnvLevel);
            } catch (error) {
                console.error('Error fetching site environmental level:', error);
            }
        };

        fetchSiteEnvLevel();
    }, [project.siteEnvironmentalLevelId, window.localStorage.getItem("i18nextLng")]);

    const translateCoefficient = (coefficient: number | undefined): string => {
        switch (coefficient ?? 0) {
            case 1:
                return t("anchor_bar_coefficient_options.no_increment");
            case 1.1:
                return t("anchor_bar_coefficient_options.drilling_in_rock");
            case 1.2:
                return t("anchor_bar_coefficient_options.drilling_in_soil");
            default:
                return '';
        }
    }

    const rockSlopeData = [
        [t('rock_slope_inclination', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_inclination'), project.rockSlope ? project.rockSlope.inclination.toString() : '', getMeasurementUnit(conversionLabel.deg)],
        [t('rock_slope_thickness_surficial_instability', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_thickness_surficial_instability'), project.rockSlope ? project.rockSlope.thicknessSurficialInstability.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('rock_slope_rock_density', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_rock_density'), project.rockSlope ? project.rockSlope.rockDensity.toString() : '', getMeasurementUnit(conversionLabel.kNm3_lbfft3)],
        [t('rock_slope_dip_set_joints', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_dip_set_joints'), project.rockSlope ? project.rockSlope.dipSetJoints.toString() : '', getMeasurementUnit(conversionLabel.deg)],
        [t('rock_slope_compressive_strength_critical_joint', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_compressive_strength_critical_joint'), project.rockSlope ? project.rockSlope.compressiveStrengthCriticalJoint.toString() : '', getMeasurementUnit(conversionLabel.mpa)],
        [t('rock_slope_roughness_coefficient_critical_joint', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_roughness_coefficient_critical_joint'), project.rockSlope ? project.rockSlope.roughnessCoefficientCriticalJoint.toString() : '', ''],
        [t('rock_slope_horizontal_seismic_coefficient', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_horizontal_seismic_coefficient'), project.rockSlope ? project.rockSlope.horizontalSeismicCoefficient.toString() : '', ''],
        [t('rock_slope_vertical_seismic_coefficient', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_vertical_seismic_coefficient'), project.rockSlope ? project.rockSlope.verticalSeismicCoefficient.toString() : '', ''],
        [t('rock_slope_external_force', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_external_force'), project.rockSlope ? project.rockSlope.externalForce.toString() : '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('rock_slope_angle_force_slope', { unit_measure: '' }), t('rock_slope_symbols.rock_slope_angle_force_slope'), project.rockSlope ? project.rockSlope.angleForceSlope.toString() : '', getMeasurementUnit(conversionLabel.deg)],
    ];

    const siteEnvLevelData = [
        [t("site_environmental_level", { unit_measure: '' }), selectedSiteEnvLevel?.name || ''],
        [t('site_environmental_level_coating', { unit_measure: '' }), selectedSiteEnvLevel?.coatings.join('\n') || ''],
    ];

    const anchorBarData = [
        [t('anchor_bar_horizontal_spacing', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_horizontal_spacing'), project.anchorBar ? project.anchorBar.horizontalSpacing.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('anchor_bar_vertical_spacing', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_vertical_spacing'), project.anchorBar ? project.anchorBar.verticalSpacing.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('anchor_bar_inclination', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_inclination'), project.anchorBar ? project.anchorBar.inclination.toString() : '', getMeasurementUnit(conversionLabel.deg)],
        [t('anchor_bar_coefficient', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_coefficient'), translateCoefficient(project.anchorBar?.coefficient), ''],
        [t('anchor_bar_anchor_type', { unit_measure: '' }), '', project.anchorBar ? project.anchorBar.anchorTypeName : '', ''],
        [t('anchor_bar_inner_diameter', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_inner_diameter'), project.anchorBar ? project.anchorBar.innerDiameter.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('anchor_bar_external_diameter', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_external_diameter'), project.anchorBar ? project.anchorBar.externalDiameter.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('anchor_bar_drilling_diameter', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_drilling_diameter'), project.anchorBar ? project.anchorBar.drillingDiameter.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('anchor_bar_yield_tensile_stress', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_yield_tensile_stress'), project.anchorBar ? project.anchorBar.yieldTensileStress.toString() : '', getMeasurementUnit(conversionLabel.mpa_ksi)],
        [t('anchor_bar_custom_thickness', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_custom_thickness'), project.anchorBar ? project.anchorBar.customThickness.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        /*  [t('anchor_bar_automatic_thickness', { unit_measure: '' }), '', project.anchorBar ? project.anchorBar.automaticThickness.toString() : '', ''],
         [t('anchor_bar_environmental_aggressivity', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_environmental_aggressivity'), project.anchorBar ? project.anchorBar.environmentalAgressivity : '', ''],
         [t('anchor_bar_lifespan', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_lifespan'), project.anchorBar ? project.anchorBar.lifespan.toString() : '', getMeasurementUnit(conversionLabel.years)],
         [t('anchor_bar_NFP94270', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_NFP94270'), project.anchorBar ? project.anchorBar.NFP94270.toString() : '', ''], */
        [t('anchor_bar_bond_stress', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_bond_stress'), project.anchorBar ? project.anchorBar.bondStress.toString() : '', getMeasurementUnit(conversionLabel.mpa_psi)],
        [t('anchor_bar_assumed_plasticization', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_assumed_plasticization'), project.anchorBar ? project.anchorBar.assumedPlasticization.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
    ];

    const meshData = [
        [t('mesh_type'), '', project.mesh ? project.mesh.meshTypeName : '', ''],
        [t('mesh_nominal_punching_resistance', { unit_measure: '' }), t('mesh_nominal_punching_resistance_symbol'), project.mesh ? project.mesh.nominalPunchingResistance.toString() : '', getMeasurementUnit(conversionLabel.kN_lbf)],
    ];

    const safetyCoefficientsData = [
        [t('safety_coefficient_uncertainty_thickness_surficial_instability', { unit_measure: '' }), t('safety_coefficient_uncertainty_thickness_surficial_instability_symbol'), project.safetyCoefficients ? project.safetyCoefficients.uncertaintyThicknessSurficialInstability.toString() : ''],
        [t('safety_coefficient_uncertainty_rock_mass_unit_weight', { unit_measure: '' }), t('safety_coefficient_uncertainty_rock_mass_unit_weight_symbol'), project.safetyCoefficients ? project.safetyCoefficients.uncertaintyRockMassUnitWeight.toString() : ''],
        [t('safety_coefficient_uncertainty_rock_behavior_and_weathering', { unit_measure: '' }), t('safety_coefficient_uncertainty_rock_behavior_and_weathering_symbol'), project.safetyCoefficients ? project.safetyCoefficients.uncertaintyRockBehaviorAndWeathering.toString() : ''],
        [t('safety_coefficient_gR', { unit_measure: '' }) + "("+t('safety_coefficient_gR_formula', { unit_measure: '' }).replaceAll("⨯", "x")+")", t('safety_coefficient_gR_symbol'), project.safetyCoefficients ? project.safetyCoefficients.gR.toString() : ''],
        [t('safety_coefficient_slope_surface_morphology', { unit_measure: '' }), t('safety_coefficient_slope_surface_morphology_symbol'), project.safetyCoefficients ? project.safetyCoefficients.slopeSurfaceMorphology.toString() : ''],
        [t('safety_coefficient_external_loads', { unit_measure: '' }), t('safety_coefficient_external_loads_symbol'), project.safetyCoefficients ? project.safetyCoefficients.externalLoads.toString() : ''],
        [t('safety_coefficient_yD', { unit_measure: '' }) + "("+t('safety_coefficient_yD_formula', { unit_measure: '' }).replaceAll("⨯", "x")+")", t('safety_coefficient_yD_symbol'), project.safetyCoefficients ? project.safetyCoefficients.yD.toString() : ''],
        [t('safety_coefficient_yDF', { unit_measure: '' }) + "("+t('safety_coefficient_yDF_formula', { unit_measure: '' }).replaceAll("⨯", "x")+")", t('safety_coefficient_yDF_symbol'), project.safetyCoefficients ? project.safetyCoefficients.yDF.toString() : ''],
        [t('safety_coefficient_coefficient_punch_resistance', { unit_measure: '' }), t('safety_coefficient_coefficient_punch_resistance_symbol'), project.safetyCoefficients ? project.safetyCoefficients.coefficientPunchResistance.toString() : ''],
        [t('safety_coefficient_coefficient_yield_stress_bar', { unit_measure: '' }), t('safety_coefficient_coefficient_yield_stress_bar_symbol'), project.safetyCoefficients ? project.safetyCoefficients.coefficientYieldStressBar.toString() : ''],
        [t('safety_coefficient_coefficient_bond_stress', { unit_measure: '' }), t('safety_coefficient_coefficient_bond_stress_symbol'), project.safetyCoefficients ? project.safetyCoefficients.coefficientBondStress.toString() : ''],
    ];

    const analysisOfSlidingOfTheSurficialZoneData = [
        [t('general_results_sum_of_the_stabilizing_forces_design_value', { unit_measure: '' }), results?.analysisOfSlidingOfTheSurficialZone.sumOfTheStabilizingForcesDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_force_acting_on_the_anchor_design_value', { unit_measure: '' }), results?.analysisOfSlidingOfTheSurficialZone.forceActingOnTheAnchorDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_factor_of_resistance', { unit_measure: '' }), results?.analysisOfSlidingOfTheSurficialZone.factorOfResistance.toString() || '', ' '],
    ];

    const analysisChecks = [
        [t('general_results_factor_of_resistance'), results?.analysisOfSlidingOfTheSurficialZone.checkFosr == 'Satisfied'],
    ];

    const anchorWorkingRateData = [
        [t('general_results_force_acting_of_the_anchor_design_value'), results?.anchorWorkingRate.forceActingOfTheAnchorDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_capacity_of_the_anchor_design_value'), results?.anchorWorkingRate.capacityOfTheAnchorDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_anchor_working_rate'), results?.anchorWorkingRate.anchorWorkingRate.toString() || '', '%'],
        [t('general_results_angle_between_anchor_axis_and_horizontal'), results?.anchorWorkingRate.angleBetweenAnchorAxisAndHorizontal.toString() || '', ' [°]'],
        [t('general_results_yield_stress_design_value'), results?.anchorWorkingRate.yieldStressDesignValue.toString() || '', getMeasurementUnit(conversionLabel.mpa_ksi)],
        [t('general_results_tensile_capacity_of_the_bar_design_value'), results?.anchorWorkingRate.tensileCapacityOfTheBarDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_effective_area_of_the_anchor_bar'), results?.anchorWorkingRate.effectiveAreaOfTheAnchorBar.toString() || '', getMeasurementUnit(conversionLabel.mm2_in2)],
        [t('general_results_nominal_diameter_of_drilling'), results?.anchorWorkingRate.nominalDiameterOfDrilling.toString() || '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('general_results_pullout_force_related_to_the_mesh'), results?.anchorWorkingRate.pulloutForceRelatedToTheMesh.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_pullout_force_related_to_the_sliding_of_the_surficial_zone'), results?.anchorWorkingRate.pulloutForceRelatedToTheSlidingOfTheSurficialZone.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_maximum_pullout_force_greater_between_a_and_b'), results?.anchorWorkingRate.maximumPulloutForceGreaterBetweenAandB.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_length_of_the_anchor_in_stable_zone'), results?.anchorWorkingRate.lengthOfTheAnchorInStableZone.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('general_results_length_of_the_anchor_in_the_unstable_zone'), results?.anchorWorkingRate.lengthOfTheAnchorInTheUnstableZone.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('general_results_length_of_the_anchor_in_plasticized_zone'), results?.anchorWorkingRate.lengthOfTheAnchorInPlasticizedZone.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('general_results_minimum_length_of_the_anchor'), results?.anchorWorkingRate.minimumLengthOfTheAnchor.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
    ];

    const analysisAnchorChecks = [
        [t('general_results_anchor_working_rate'), results?.analysisOfSlidingOfTheSurficialZone?.checkAnchorWorkingRate == 'Satisfied'],
    ];

    const resultsMeshData = [
        [t('general_results_punch_resistance_of_the_mesh_design_value'), results?.mesh.punchResistanceOfTheMeshDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_punch_force_acting_on_the_mesh_design_value'), results?.mesh.punchForceActingOnTheMeshDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_factor_of_resistance_for_the_punching_of_the_mesh'), results?.mesh.factorOfResistanceForThePunchingOfTheMesh.toString() || '', ' '],
        [t('general_results_rock_volume_that_can_slide_among_the_anchors'), results?.mesh.rockVolumeThatCanSlideAmongTheAnchors.toString() || '', getMeasurementUnit(conversionLabel.m3_ft3)],
        [t('general_results_rock_weight_that_can_slide_among_anchors'), results?.mesh.rockWeightThatCanSlideAmongAnchors.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_sum_of_driving_forces_acting_on_the_sliding_plane_design_value'), results?.mesh.sumOfDrivingForcesActingOnTheSlidingPlaneDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_sum_of_resisting_forces_acting_on_the_sliding_plane_design_value'), results?.mesh.sumOfResistingForcesActingOnTheSlidingPlaneDesignValue.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_nominal_displacement_calculated'), results?.mesh.nominalDisplacementCalculated || '', getMeasurementUnit(conversionLabel.m_ft)],
    ];

    const meshChecks = [
        [t('general_results_factor_of_resistance_for_the_punching_of_the_mesh'), results?.outputMeshAnalysis.checkFoSr == 'Satisfied'],
    ];

    component = <></>;

    return (
        <Document>
            <Page size='A4' style={window.localStorage.getItem("i18nextLng") == "zh-CN" ? styles.pageZh : styles.pageDefault}>
                <PDFHeader projectInfo={projectInfo} />

                <View>
                    <ProjectInformation projectInfo={projectInfo} results={results} />
                </View>

                <View>
                    <Text style={styles.title} >{t('output_table_title_input')}</Text>
                </View>

                <View style={styles.body} >
                    <View style={styles.flexColumn50}>
                        <View>
                            <Text style={styles.boldGreen} >
                                {t('input_form_accordion_rock_slope')}
                            </Text>
                            <PDFTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={rockSlopeData} />
                        </View>
                    </View>
                    <View style={styles.flexColumn50}>
                        <Image source={imgSrc}></Image>
                    </View>
                </View>

                <View>
                    <Text style={styles.boldGreen} break>
                        {t("input_form_accordion_site_env_level")}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_value')]} data={siteEnvLevelData} />

                    {/* <View style={styles.mt5}>
                    {(selectedSiteEnvLevel?.coatings?.length ?? 0) > 0 && (
                            selectedSiteEnvLevel?.coatings.map((coating: string) => (
                                <Text style={styles.subtitle} key={coating}>{coating}</Text>
                            ))
                        )}
                    </View> */}
                </View>

                <View>
                    <Text style={styles.boldGreen} break>
                        {t('input_form_accordion_anchor_bar')}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={anchorBarData} />
                </View>

                <View>
                    <Text style={styles.boldGreen} break>
                        {t('input_form_accordion_mesh')}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={meshData} />
                </View>

                <View>
                    <Text style={styles.boldGreen} break>
                        {t('input_form_accordion_safety_coefficients')}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_symbol'), t('output_value')]} data={safetyCoefficientsData} />
                </View>

                <View break>
                    <Text style={styles.title}>{t('graphical_results_general_results')}</Text>
                </View>

                <View style={styles.body}>

                    <View style={styles.resultBoxTitle}>
                        <Text style={styles.checkGroupTitle}>
                            {t('general_results_analysis_of_sliding_of_the_surficial_zone_data')}
                        </Text>
                    </View>

                    <View style={[styles.resultBoxTitle, styles.bl0]}>
                        <Text style={styles.checkGroupTitle}>
                            {t('general_results_mesh_analysis')}
                        </Text>
                    </View>

                    <View style={styles.resultBox}>
                        <Text style={styles.checkTitle}>
                            {t('pdf_check_title_fos')}<Text style={styles.subscript}>{t("pdf_check_title_r")}</Text>
                        </Text>
                        <Text style={{ marginTop: 'auto' }}>
                            {results?.analysisOfSlidingOfTheSurficialZone.checkFosr === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.analysisOfSlidingOfTheSurficialZone?.factorOfResistance}  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.analysisOfSlidingOfTheSurficialZone?.factorOfResistance}  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>

                    <View style={[styles.resultBox, styles.bl0]}>
                        <Text style={styles.checkTitle}>
                            {t('pdf_check_title_anchor_working_rate')}
                        </Text>

                        <Text style={{ marginTop: 'auto' }}>
                            {results?.analysisOfSlidingOfTheSurficialZone?.checkAnchorWorkingRate === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.analysisOfSlidingOfTheSurficialZone?.anchorWorkingRate}%  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.analysisOfSlidingOfTheSurficialZone?.anchorWorkingRate}%  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>
                    <View style={[styles.resultBox, styles.bl0]}>
                        <Text style={styles.checkTitle}>
                            {t('pdf_check_title_fos')}<Text style={styles.subscript}>{t("pdf_check_title_r")}</Text>
                        </Text>

                        <Text style={{ marginTop: 'auto' }}>
                            {results?.outputMeshAnalysis.checkFoSr === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.outputMeshAnalysis.foSr}  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.outputMeshAnalysis.foSr}  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>
                    <View style={[styles.resultBox, styles.bl0]}>
                        <Text style={styles.checkTitle}>
                            {t('pdf_check_title_working_tree')}
                        </Text>
                        <Text style={{ marginTop: 'auto' }}>


                            {results?.outputMeshAnalysis.checkWorkingRate === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.outputMeshAnalysis.workingRate}%  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.outputMeshAnalysis.workingRate}%  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>

                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_analysis_of_sliding_of_the_surficial_zone_data')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={analysisOfSlidingOfTheSurficialZoneData} checks={analysisChecks} />
                    </View>

                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_anchor_working_rate_data')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={anchorWorkingRateData} checks={analysisAnchorChecks} />
                    </View>

                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_mesh_design')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={resultsMeshData} checks={meshChecks} />
                    </View>

                </View>

                <PDFFooter />
            </Page>
        </Document>
    );
};

export default PDFMacro1;
